/**
 * API Helper Utilities
 *
 * Centralized utilities for RTK Query API configuration
 * Provides standardized patterns for cache tags, response transformation, and error handling
 */

import { CACHE_TIME } from "../../config/constants";

// Re-export CACHE_TIME for backward compatibility
export { CACHE_TIME };

/**
 * Generate cache tag for list endpoints with date range
 * Ensures proper cache invalidation based on query parameters
 * venueId is handled by backend from JWT, so not needed in cache tags
 */
export const generateListCacheTag = (
  type: string,
  startDate?: string,
  endDate?: string
): string => {
  const parts = [type];
  if (startDate && endDate) parts.push(`${startDate}-${endDate}`);
  return parts.join("-");
};

/**
 * Generate cache tag for paginated endpoints
 * Includes date range and page number for granular caching
 * venueId is handled by backend from JWT, so not needed in cache tags
 */
export const generatePaginationCacheTag = (
  type: string,
  page: number,
  startDate?: string,
  endDate?: string
): string => {
  const parts = [type, `page-${page}`];
  if (startDate && endDate) parts.push(`${startDate}-${endDate}`);
  return parts.join("-");
};

/**
 * Transform API response to extract data
 * Standardizes response structure across all endpoints
 */
export const extractData = <T>(response: { success: boolean; data: T }): T => {
  return response.data;
};

/**
 * Transform paginated API response
 * Normalizes pagination structure
 */
export const extractPaginatedData = <T>(response: {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}) => {
  return {
    items: response.data,
    pagination: response.pagination,
  };
};

/**
 * Create standard cache tags for a list response
 * Generates both list-level and item-level tags
 */
export const createListCacheTags = <T extends { id: string }>(
  type: "Auth" | "User" | "Messages" | "Payments" | "Analytics" | "Tickets",
  result: { success: boolean; data: T[] } | undefined
): Array<{ type: typeof type; id: string }> => {
  if (!result) {
    return [{ type, id: "LIST" }];
  }

  return [
    ...result.data.map((item) => ({ type, id: item.id })),
    { type, id: "LIST" },
  ];
};

/**
 * Create cache invalidation tags for mutations
 * Returns tags that should be invalidated when data changes
 */
export const createInvalidationTags = (
  types: string[],
  ids: Array<string | "LIST"> = ["LIST"]
): Array<{ type: string; id: string }> => {
  return types.flatMap((type) => ids.map((id) => ({ type, id })));
};

/**
 * Standard keepUnusedDataFor values
 * Imported from centralized constants - see src/config/constants.ts
 * Re-exported above for backward compatibility
 */

/**
 * Check if cache should be skipped based on time threshold
 * Useful for implementing smart cache invalidation
 */
export const shouldSkipCache = (
  cachedTimestamp: number | null,
  ttl: number = CACHE_TIME.MEDIUM
): boolean => {
  if (!cachedTimestamp) return false;
  return Date.now() - cachedTimestamp < ttl * 1000;
};
